import useSWR, { mutate } from 'swr';

import { useFsWatch } from '@/api-models/sandbox-fs-watch';
import { fetchEndpointData } from '@/utils/fetch.client';

export interface GitFileStatus {
  path: string;
  staging: string;
  worktree: string;
}

export interface GitStatus {
  current_branch: string;
  ahead: number;
  behind: number;
  branch_published: boolean;
  file_status: GitFileStatus[];
}

/** Mirrors the backend's `BranchDiffFile`; `status` is Added/Modified/Deleted/Renamed/Copied. */
export interface BranchDiffFile {
  path: string;
  status: string;
}

/** Every git-backed view for a session — commits, status, branch diff, the PR lookup and any
 * expanded file diffs — keys off the same `${base}/git/` prefix, so one filter revalidates them all. */
export function revalidateGitViews(base: string) {
  return mutate((key: unknown) => typeof key === 'string' && key.startsWith(`${base}/git/`));
}

function statusKey(base: string) {
  return `${base}/git/status`;
}

function branchDiffKey(base: string, baseRef: string) {
  return `${base}/git/branch-diff?base=${encodeURIComponent(baseRef)}`;
}

export function useGitStatus(base: string | null) {
  const key = base ? statusKey(base) : null;
  const { data, error, isLoading } = useSWR(key, () => fetchEndpointData<GitStatus>(key!), {
    revalidateOnFocus: false,
  });
  useFsWatch(base, key ? [key] : []);
  return { status: data ?? null, isLoading, error };
}

export function useGitBranchDiff(base: string | null, baseRef = 'main') {
  const key = base ? branchDiffKey(base, baseRef) : null;
  const { data, error, isLoading } = useSWR(key, () => fetchEndpointData<{ files: BranchDiffFile[] }>(key!), {
    revalidateOnFocus: false,
  });
  useFsWatch(base, key ? [key] : []);
  return { files: data?.files ?? [], isLoading, error };
}

/**
 * Raw unified diff for one file, against the merge-base with `baseRef` — plain
 * `git diff` would be empty for anything the agent has already committed. Only
 * fetched once its card is expanded.
 */
export function useGitFileDiff(base: string | null, path: string | null, baseRef = 'main') {
  const key =
    base && path ? `${base}/git/diff?path=${encodeURIComponent(path)}&base=${encodeURIComponent(baseRef)}` : null;
  const { data, error, isLoading } = useSWR(key, () => fetchEndpointData<{ diff: string }>(key!), {
    revalidateOnFocus: false,
  });
  return { diff: data?.diff ?? null, isLoading, error };
}

/** Mirrors the backend's `Commit` (`services::source_provider::Commit`). */
export interface Commit {
  sha: string;
  message: string;
  author_name: string;
  author_avatar_url: string | null;
  committed_at: string;
  html_url: string;
}

/** Mirrors the backend's `GitCommitsResponse`. */
export interface GitCommits {
  base: string;
  head: string;
  commits: Commit[];
  /** Provider reason the comparison failed — usually an unpushed branch. `commits` is empty then. */
  unavailable: string | null;
}

/**
 * Commits on the session branch as the provider sees them, so the list matches
 * what a reviewer would see on the PR. An unpushed branch therefore has nothing
 * to show and comes back with `unavailable` set rather than as an error.
 */
export function useGitCommits(base: string | null, baseRef = 'main') {
  const key = base ? `${base}/git/commits?base=${encodeURIComponent(baseRef)}` : null;
  const { data, error, isLoading } = useSWR(key, () => fetchEndpointData<GitCommits>(key!), {
    revalidateOnFocus: false,
  });
  useFsWatch(base, key ? [key] : []);
  return { commits: data?.commits ?? [], unavailable: data?.unavailable ?? null, isLoading, error };
}

/** Mirrors the backend's `PullRequestState`. */
export type PullRequestState = 'open' | 'closed' | 'merged' | 'draft';

/** Mirrors the backend's `PullRequest`. */
export interface PullRequest {
  number: number;
  title: string;
  /** The PR description. `null` when the provider omitted it. */
  body: string | null;
  state: PullRequestState;
  author: string;
  author_avatar_url: string | null;
  head_ref: string;
  base_ref: string;
  html_url: string;
  created_at: string;
  updated_at: string;
  comments: number;
  additions: number | null;
  deletions: number | null;
}

/** Mirrors the backend's `SessionPullRequestResponse`. */
export interface SessionPullRequest {
  head: string;
  pull: PullRequest | null;
  /** Provider reason the lookup failed. `pull` is then `null` meaning *unknown*, not *none*. */
  unavailable: string | null;
}

/** SWR key for a session's own pull request. Shared so the poll and the push can't disagree. */
export function sessionPullRequestKey(base: string): string {
  return `${base}/git/pull-request`;
}

/**
 * The agent's own pull-request tools (`services/mcp/repo.rs`) that change a PR's existence or
 * state. A successful call to one of these makes every cached view of that PR stale.
 */
const PR_MUTATING_TOOLS = new Set([
  'mcp__repo__create_pull_request',
  'mcp__repo__update_pull_request',
  'mcp__repo__merge_pull_request',
  'mcp__repo__close_pull_request',
]);

export function isPullRequestMutatingTool(toolName: string): boolean {
  return PR_MUTATING_TOOLS.has(toolName);
}

/**
 * Refresh every cached view of a session's pull request, now.
 *
 * Called the instant one of the agent's PR tools returns, which is what keeps the header chip
 * from lagging up to a poll interval behind a PR the agent just opened. A revalidate rather than
 * a cache write: the tool answers with a trimmed PR (enough for the model), while these caches
 * hold the full provider shape the Review tab renders — writing the small one in would blank
 * fields that are merely absent, not changed.
 */
export async function revalidateSessionPullRequest(sessionId: string): Promise<void> {
  await Promise.all([
    mutate(sessionPullRequestKey(`/api/v1/agent-sessions/${sessionId}`)),
    // The team-wide map behind the sessions list, whose own refresh is a lazy 60s.
    mutate((key) => typeof key === 'string' && key.startsWith('/api/v1/agent-sessions/pull-requests')),
  ]);
}

/**
 * The open PR for the session branch, whoever opened it.
 *
 * Polled because a PR can appear without this client doing anything — a teammate opens one, or
 * the user pushes from elsewhere. The agent's own PRs no longer wait for that poll: its tools run
 * in the API process and their results arrive over the session socket, which calls
 * {@link revalidateSessionPullRequest} immediately. The timer is the safety net, not the path.
 */
export function useSessionPullRequest(base: string | null) {
  const key = base ? sessionPullRequestKey(base) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<SessionPullRequest>(key!), {
    refreshInterval: 30_000,
  });
  return { pull: data?.pull ?? null, unavailable: data?.unavailable ?? null, isLoading, error, mutate };
}

/** Mirrors the backend's `SessionPullRequest` (`routes/agent_sessions/pull_requests.rs`). */
export interface TeamSessionPullRequest {
  session_id: string;
  number: number;
  state: PullRequestState;
  title: string;
  html_url: string;
}

/**
 * PR state for every session in a team, keyed by session id.
 *
 * One provider call per repository rather than one per session, which is what makes it cheap
 * enough for list views to hold open. Best-effort on the server: a session missing from the map
 * has no PR *that we could see*, so never offer "create a PR" on the strength of this — the
 * per-session {@link useSessionPullRequest} distinguishes "none" from "couldn't ask".
 *
 * Slow refresh on purpose: a PR opening or merging is a human-paced event, and the sessions list
 * beside it already polls every five seconds.
 */
export function useTeamPullRequests(teamId: string | undefined) {
  const key = teamId ? `/api/v1/agent-sessions/pull-requests?team_id=${encodeURIComponent(teamId)}` : null;
  const { data, error, isLoading } = useSWR(key, () => fetchEndpointData<TeamSessionPullRequest[]>(key!), {
    refreshInterval: 60_000,
  });

  const bySession = new Map((data ?? []).map((pr) => [pr.session_id, pr]));
  return { pullsBySession: bySession, isLoading, error };
}

export interface GitPushResult {
  branch: string;
  /** Git's own output — carries the remote's ref-update line. */
  output: string;
}

/** Publish the session's branch. Always sets upstream, so the first push works. */
export async function pushBranch(base: string): Promise<GitPushResult> {
  return fetchEndpointData<GitPushResult>(`${base}/git/push`, { method: 'POST' });
}

/**
 * Push the branch and open a pull request against it.
 *
 * The server pushes first — a PR referencing an unpushed ref is a provider 422 with an unhelpful
 * message, and the two steps have no separate meaning to whoever clicked the button. Opens as a
 * draft unless told otherwise.
 */
export async function createPullRequest(
  base: string,
  payload: { title?: string; body?: string; base?: string; draft?: boolean } = {},
): Promise<PullRequest> {
  return fetchEndpointData<PullRequest>(`${base}/git/pull-request`, { method: 'POST', body: payload });
}

/**
 * Edit an existing pull request's title/body in place.
 *
 * Idempotent, unlike {@link createPullRequest}: the Review tab uses this to save edits to a PR
 * that already exists rather than sending the user to the provider. `number` lets the server skip
 * a lookup; it falls back to finding the branch's PR when omitted.
 */
export async function updatePullRequest(
  base: string,
  payload: { number?: number; title?: string; body?: string },
): Promise<PullRequest> {
  return fetchEndpointData<PullRequest>(`${base}/git/pull-request`, { method: 'PATCH', body: payload });
}

/** AI-drafted PR copy: a conventional-commit title and a markdown body. */
export interface GeneratedPullRequest {
  title: string;
  body: string;
}

/**
 * Draft a PR title and description from the branch's diff and commits via the LLM gateway.
 *
 * Reads the diff from the sandbox worktree, so it works before the branch is pushed. Slow (a
 * model round-trip) — always drive it from an explicit user action with a loading state.
 */
export async function generatePullRequest(base: string): Promise<GeneratedPullRequest> {
  return fetchEndpointData<GeneratedPullRequest>(`${base}/git/pull-request/generate`, { method: 'POST' });
}

/** Result of {@link startReview}. */
export interface StartReviewResult extends GeneratedPullRequest {
  /** Whether outstanding work was committed as part of starting the review. */
  committed: boolean;
  /** SHA of the checkpoint commit, when one was made. */
  sha: string | null;
}

/**
 * Begin a review in one action: commit whatever the agent left uncommitted, then draft the PR
 * title and description. The checkpoint commit reuses the generated title as its message. Slow (a
 * git commit plus a model round-trip) — always drive it from an explicit user action.
 */
export async function startReview(base: string): Promise<StartReviewResult> {
  return fetchEndpointData<StartReviewResult>(`${base}/git/review/start`, { method: 'POST' });
}

/** How to land a pull request. Mirrors the backend's `MergeMethod`. */
export type MergeMethod = 'merge' | 'squash';

/**
 * Merge the branch's pull request with the given strategy, returning the updated PR so the caller
 * can reflect the merged state. `number` lets the server skip a lookup; it falls back to the
 * branch's PR when omitted.
 */
export async function mergePullRequest(
  base: string,
  payload: { method: MergeMethod; number?: number },
): Promise<PullRequest> {
  return fetchEndpointData<PullRequest>(`${base}/git/pull-request/merge`, { method: 'POST', body: payload });
}

/** Close the branch's pull request without merging, returning the updated PR. */
export async function closePullRequest(base: string, payload: { number?: number } = {}): Promise<PullRequest> {
  return fetchEndpointData<PullRequest>(`${base}/git/pull-request/close`, { method: 'POST', body: payload });
}
